import { XMLParser, XMLBuilder } from "fast-xml-parser"
import { cleanUpRating, ColorTag, isColorTag } from "./exif"
import { PhotoFlagStatus, PhotoMetadataRecord, PhotoRecord, Rating } from "@/lib/catalog"
import { decimalToFraction } from "./math"

const parser = new XMLParser({
	ignoreAttributes: false,
	attributeNamePrefix: "@_",
})

const builder = new XMLBuilder({
	ignoreAttributes: false,
	attributeNamePrefix: "@_",
	format: true,
	indentBy: "  ",
	suppressEmptyNode: false,
	suppressBooleanAttributes: false,
	preserveOrder: false,
})

export type XmpFileHandleOptions = {
	directoryHandle: FileSystemDirectoryHandle
	filename: string
	path: string
	create?: boolean
}

// Extract metadata from sidecar file
export const extractMetadataFromSidecarFile = async (file: File): Promise<object> => {
	const text = await file.text()
	const xml = parser.parse(text)
	const metadata: object = xml["x:xmpmeta"]["rdf:RDF"]["rdf:Description"] ?? {}

	return metadata
}

export const getXmpMetadata = async (options: XmpFileHandleOptions) => {
	const xmpFile = await getXmpFile(options)
	if (xmpFile) {
		const xmpMetadata = await extractMetadataFromSidecarFile(xmpFile)
		if (xmpMetadata) {
			const rating = getSidecarRating(xmpMetadata)
			const colorTag = getSidecarColorTag(xmpMetadata)
			const flag = getSidecarFlag(xmpMetadata)
			return { rating, colorTag, flag }
		}
	}

	return null
}

// Get rating from sidecar metadata
export const getSidecarRating = (metadata: object): Rating | null => {
	try {
		const ratingContent = getSidecarParameter<string>(metadata, "xmp:Rating") as string
		return cleanUpRating(ratingContent)
	} catch (error) {
		console.error("Error getting sidecar rating:", error)
		return null
	}
}

// Set rating in sidecar metadata
export const setSidecarRating = (metadata: object, rating: Rating) => {
	try {
		if (rating === 0) {
			delete metadata["@_xmp:Rating"]
			delete metadata["xmp:Rating"]
		} else {
			setSidecarParameter(metadata, "xmp:Rating", rating)
		}
	} catch (error) {
		console.error("Error setting sidecar rating:", error)
	}
}

// Get Flag from sidecar metadata
export const getSidecarFlag = (metadata: object): PhotoFlagStatus => {
	try {
		const pick = getSidecarParameter<number>(metadata, "xmpDM:pick")
		const good = getSidecarParameter<string>(metadata, "xmpDM:good")
		if (pick === 1 || good?.toLocaleLowerCase() === "true") {
			return "flagged"
		} else if (pick === -1 || good?.toLocaleLowerCase() === "false") {
			return "rejected"
		} else {
			// (pick === 0)
			return "unflagged"
		}
	} catch (error) {
		console.error("Error getting sidecar flag:", error)
		return "unflagged"
	}
}

// Set Flag in sidecar metadata
export const setSidecarFlag = (metadata: object, flag: PhotoFlagStatus) => {
	try {
		if (flag === "flagged") {
			setSidecarParameter(metadata, "xmpDM:pick", 1)
			setSidecarParameter(metadata, "xmpDM:good", "true")
		} else if (flag === "rejected") {
			setSidecarParameter(metadata, "xmpDM:pick", -1)
			setSidecarParameter(metadata, "xmpDM:good", "false")
		} else if (flag === "unflagged") {
			delete metadata["@_xmpDM:good"]
			delete metadata["xmpDM:good"]
			setSidecarParameter(metadata, "xmpDM:pick", 0)
		}
	} catch (error) {
		console.error("Error setting sidecar flag:", error)
	}
}

// Get color tag from sidecar metadata
export const getSidecarColorTag = (metadata: object): ColorTag | null => {
	try {
		const colorTag = getSidecarParameter<string>(metadata, "xmp:Label")

		if (isColorTag(colorTag)) {
			return colorTag as ColorTag
		} else {
			return null
		}
	} catch (error) {
		console.error("Error getting sidecar color tag:", error)
		return null
	}
}

// Set color tag in sidecar metadata
export const setSidecarColorTag = (metadata: object, colorTag: ColorTag) => {
	try {
		if (colorTag === "No Color") {
			delete metadata["@_xmp:Label"]
			delete metadata["xmp:Label"]
		} else {
			setSidecarParameter(metadata, "xmp:Label", colorTag)
		}
	} catch (error) {
		console.error("Error setting sidecar color tag:", error)
	}
}

// Get ISO from sidecar metadata
export const getSidecarISO = (metadata: object): number | null => {
	try {
		const iso = metadata?.["@_exif:ISOSpeedRatings"] ?? metadata?.["exif:ISOSpeedRatings"]
		if (iso && Number.isInteger(iso["rdf:Seq"]["rdf:li"])) {
			return iso["rdf:Seq"]["rdf:li"] as number
		}
		return null
	} catch (error) {
		console.error("Error getting sidecar ISO:", error)
		return null
	}
}

const setSidecarISO = (metadata: object, iso: number | null) => {
	if (iso === null) {
		return
	}
	try {
		if (!metadata["exif:ISOSpeedRatings"]) {
			metadata["exif:ISOSpeedRatings"] = {
				"rdf:Seq": {
					"rdf:li": iso,
				},
			}
		} else {
			metadata["exif:ISOSpeedRatings"]["rdf:Seq"]["rdf:li"] = iso
		}
	} catch (error) {
		console.error("Error setting sidecar ISO:", error)
	}
}

const setSidecarFlash = (metadata: object, flash: number | null) => {
	if (flash === null) {
		return
	}
	const flashValue = flash === 1 ? "True" : "False"
	try {
		if (!metadata["exif:Flash"]) {
			metadata["exif:Flash"] = {
				"@_Fired": flashValue,
			}
		} else {
			metadata["exif:ISOSpeedRatings"]["Flash"] = flashValue
		}
	} catch (error) {
		console.error("Error setting sidecar Flash:", error)
	}
}

const setSidecarParameter = (
	metadata: object,
	key: string,
	value?: string | number | object | null,
) => {
	if (value === undefined || value === null) {
		return
	}
	try {
		metadata["@_" + key] = value
	} catch (error) {
		console.error("Error setting sidecar error:", key, value, error)
	}
}

const getSidecarParameter = <T>(metadata: object, key: string): T | null => {
	try {
		return ((metadata?.["@_" + key] ?? metadata?.[key]) as T) ?? null
	} catch (error) {
		console.error("Error getting sidecar parameter:", key, error)
		return null
	}
}

export async function updateXmpFile(
	options: XmpFileHandleOptions,
	photo: PhotoRecord,
	photoMetaData?: PhotoMetadataRecord,
) {
	try {
		const fileHandle = await getXmpFileHandle(options)
		// Try to get the file handle (this will fail if the file does not exist)
		const existingFile = await fileHandle.getFile()
		let metadata = {},
			xml: object = {},
			hasXmlData = false
		if (existingFile.size !== 0) {
			const text = await existingFile.text()
			xml = parser.parse(text)

			if (xml["x:xmpmeta"]) {
				const existingMetadata = xml["x:xmpmeta"]["rdf:RDF"]["rdf:Description"]
				metadata = { ...metadata, ...existingMetadata }
				hasXmlData = true
			}
		}

		if (!hasXmlData) {
			xml = {
				"x:xmpmeta": {
					"@_xmlns:x": "adobe:ns:meta/",
					"@_x:xmptk": "Adobe XMP Core 7.0-c000 1.000000, 0000/00/00-00:00:00",
					"rdf:RDF": {
						"@_xmlns:rdf": "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
						"rdf:Description": metadata,
					},
				},
			}
		}

		// Ensure the necessary namespaces are defined
		if (!metadata["@_xmlns:xmp"]) {
			metadata["@_xmlns:xmp"] = "http://ns.adobe.com/xap/1.0/"
		}
		if (!metadata["@_xmlns:xmpDM"]) {
			metadata["@_xmlns:xmpDM"] = "http://ns.adobe.com/xmp/1.0/DynamicMedia/"
		}
		if (!metadata["@_xmlns:tiff"]) {
			metadata["@_xmlns:tiff"] = "http://ns.adobe.com/tiff/1.0/"
		}
		if (!metadata["@_xmlns:exif"]) {
			metadata["@_xmlns:exif"] = "http://ns.adobe.com/exif/1.0/"
		}
		if (!metadata["@_xmlns:aux"]) {
			metadata["@_xmlns:aux"] = "http://ns.adobe.com/exif/1.0/aux/"
		}

		if (photo.rating !== null) {
			setSidecarRating(metadata, photo.rating)
		}

		if (photo.colorTag !== null) {
			setSidecarColorTag(metadata, photo.colorTag)
		}

		if (photo.flag !== null) {
			setSidecarFlag(metadata, photo.flag)
		}

		const formatISO = (date: number) => new Date(date).toISOString().slice(0, 19)
		if (photoMetaData) {
			setSidecarISO(metadata, photoMetaData.iso)
			setSidecarFlash(metadata, photoMetaData.flash)
			setSidecarParameter(metadata, "xmp:ModifyDate", formatISO(photoMetaData.dateModified))
			setSidecarParameter(metadata, "xmp:MetadataDate", formatISO(Date.now()))

			setSidecarParameter(metadata, "tiff:Make", photoMetaData.cameraMake)
			setSidecarParameter(metadata, "tiff:Model", photoMetaData.cameraModel)
			setSidecarParameter(metadata, "tiff:ImageWidth", photoMetaData.width)
			setSidecarParameter(metadata, "tiff:ImageLength", photoMetaData.height)
			setSidecarParameter(metadata, "tiff:Orientation", photoMetaData.orientation?.toString())

			setSidecarParameter(metadata, "aux:SerialNumber", photoMetaData.cameraSerialNumber)

			setSidecarParameter(metadata, "exif:LensMake", photoMetaData.lensMake)
			setSidecarParameter(metadata, "exif:LensModel", photoMetaData.lensModel)
			setSidecarParameter(
				metadata,
				"exif:FocalLengthIn35mmFilm",
				photoMetaData.focalLength35mm,
			)

			if (photoMetaData.shutterSpeed !== null) {
				setSidecarParameter(
					metadata,
					"exif:ExposureTime",
					decimalToFraction(photoMetaData.shutterSpeed),
				)
			}

			if (photoMetaData.fNumber !== null) {
				setSidecarParameter(
					metadata,
					"exif:FNumber",
					decimalToFraction(photoMetaData.fNumber),
				)
			}
			if (photoMetaData.focalLength !== null) {
				setSidecarParameter(
					metadata,
					"exif:FocalLength",
					decimalToFraction(photoMetaData.focalLength),
				)
			}

			if (photoMetaData.dateCreated !== null) {
				// Ensure the date is in ISO 8601 format "YYYY-MM-DDTHH:MM:SS"
				const dateCreated = formatISO(photoMetaData.dateCreated)
				setSidecarParameter(metadata, "exif:DateTimeOriginal", dateCreated)
				setSidecarParameter(metadata, "xmp:CreateDate", dateCreated)
			}
		}

		// Sort metadata keys
		const sortedMetadata = Object.keys(metadata)
			.sort((a, b) => {
				if (a.startsWith("@_xmlns:") && !b.startsWith("@_xmlns:")) return -1
				if (!a.startsWith("@_xmlns:") && b.startsWith("@_xmlns:")) return 1
				return a.localeCompare(b)
			})
			.reduce((acc, key) => {
				acc[key] = metadata[key]
				return acc
			}, {})

		// Update the metadata in the XML structure
		xml["x:xmpmeta"]["rdf:RDF"]["rdf:Description"] = sortedMetadata

		// Convert the default structure to an XML string
		let newXmlData: string = builder.build(xml)

		const replaceFunc = (_match: string, tagStart: string, attributes: string) => {
			const formattedAttributes: string = attributes
				.trim()
				.replace(/\s*([a-zA-Z0-9:-]+)=/g, "\n      $1=")
			return `${tagStart}${formattedAttributes}>`
		}

		// Insert new lines between attributes
		newXmlData = newXmlData.replace(/(<rdf:Description)([^>]*?)>/g, replaceFunc)

		// Create a writable stream for the new file
		const writable = await fileHandle.createWritable()

		// Write the XML content to the file
		await writable.write(newXmlData)

		// Close the writable stream
		await writable.close()
	} catch (e) {
		console.error("Error updating XMP file:", e)
	}
}

async function getXmpFileHandle(options: XmpFileHandleOptions) {
	const xmpFileName = getXmpFileName(options.filename)
	let directoryHandle = options.directoryHandle

	if (options.path) {
		const subPaths = options.path.split("/").slice(1)
		for (const subPath of subPaths) {
			directoryHandle = await directoryHandle.getDirectoryHandle(subPath)
		}
	}

	return await directoryHandle.getFileHandle(xmpFileName, { create: options.create })
}

async function getXmpFile(options: XmpFileHandleOptions) {
	const fileHandle = await getXmpFileHandle(options)

	if (!fileHandle) {
		return null
	}

	return await fileHandle.getFile()
}

// Helper method to get XMP file name from photo file name
function getXmpFileName(photoFile: string): string {
	const fileNameWithoutExt = photoFile.substring(0, photoFile.lastIndexOf(".")) || photoFile

	return `${fileNameWithoutExt}.xmp`
}
